import React, { useState, useEffect } from 'react';
import { generalInfoService, GeneralInfo } from '../../services/general-info.service';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, User, MessageSquare, FileText } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../../services/api';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    objet: '',
    message: '',
  });
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalInfo, setGeneralInfo] = useState<GeneralInfo | null>(null);

  useEffect(() => {
    const fetchGeneralInfo = async () => {
      try {
        const data = await generalInfoService.getGeneralInfo();
        setGeneralInfo(data);
      } catch (error) {
        console.error('Error fetching general info:', error);
      }
    };
    fetchGeneralInfo();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaValue) {
      toast.error('Veuillez compléter le CAPTCHA');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send form data along with the CAPTCHA token to the backend
      await api.post('/contact', { 
        name: formData.nom + ' ' + formData.prenom, // Adjust according to your backend needs, since it expects 'name'
        email: generalInfo?.emailcontact || 'test@example.com', // Provide a fallback or add an email field to the form
        phone: 'Non fourni', // The form doesn't currently collect this, providing placeholder
        message: formData.message,
        captcha: captchaValue
      });

      toast.success('Message envoyé avec succès! Nous vous répondrons dans les plus brefs délais.');

      // Reset form
      setFormData({
        nom: '',
        prenom: '',
        objet: '',
        message: '',
      });
      setCaptchaValue(null);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du message. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Contact - SAPI</title>
      </Helmet>

      {/* Hero Section */}
      <div className="bg-white pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm font-semibold text-primary uppercase tracking-wider mb-2">CONTACT</p>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Contactez-nous
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Une question ? Une suggestion ? N'hésitez pas à nous contacter, nous serons ravis de vous répondre
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Nos Coordonnées</h2>
              <p className="text-gray-600 mb-8">
                Vous pouvez nous joindre via les moyens suivants ou remplir le formulaire ci-contre.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="space-y-6">
              {/* Phone */}
              <div className="flex items-start gap-4 p-6 bg-primary/5 rounded-xl hover:bg-primary/10 transition-colors">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Téléphone</h3>
                  <p className="text-gray-600">{generalInfo?.phone || '+213 XXX XXX XXX'}</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4 p-6 bg-secondary/5 rounded-xl hover:bg-secondary/10 transition-colors">
                <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                  <p className="text-gray-600">{generalInfo?.emailcontact || 'contact@sapi-dz.org'}</p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4 p-6 bg-accent/5 rounded-xl hover:bg-accent/10 transition-colors">
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Adresse</h3>
                  <p className="text-gray-600">{generalInfo?.address || 'Alger, Algérie'}</p>
                </div>
              </div>
            </motion.div>

            {/* Map placeholder */}
            <motion.div variants={fadeInUp} className="mt-8">
              <div className="w-full h-64 bg-gray-100 rounded-xl overflow-hidden shadow-md">
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <MapPin className="w-12 h-12" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Envoyez-nous un message</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nom & Prenom */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" />
                      Nom
                    </label>
                    <input
                      type="text"
                      name="nom"
                      required
                      value={formData.nom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                      placeholder="Votre nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" />
                      Prénom
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      required
                      value={formData.prenom}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                      placeholder="Votre prénom"
                    />
                  </div>
                </div>

                {/* Objet */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    Objet
                  </label>
                  <input
                    type="text"
                    name="objet"
                    required
                    value={formData.objet}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                    placeholder="Objet de votre message"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-primary" />
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
                    placeholder="Votre message..."
                  />
                </div>

                <div className="flex justify-center mt-4 mb-4">
                  <ReCAPTCHA
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                    onChange={(value) => setCaptchaValue(value)}
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !captchaValue}
                  className="w-full bg-primary hover:bg-primary-dark text-white font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      Envoyer le message
                    </>
                  )}
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
